import { useMemo } from 'react';
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
} from '../../../features/notifications/api';
import { NotificationEvent } from '../../../features/notifications/types';
import { toast } from 'react-toastify';
import '../settings.scss';

// Only show these events to customers
const CUSTOMER_VISIBLE_EVENTS: NotificationEvent[] = [
  'ORDER_ASSIGNED',
  'ORDER_READY_FOR_PICKUP',
  'ORDER_REJECTED',
  'ORDER_DELIVERED',
  'ORDER_PREPAY_REQUIRED',
  'ORDER_NOTE_FROM_ORG',
];

const EVENT_LABELS: Record<NotificationEvent, { title: string; description: string }> = {
  ORDER_APPROVED: {
    title: 'Order Approved',
    description: 'When your order is approved and processing begins',
  },
  ORDER_ASSIGNED: {
    title: 'Carrier Assigned',
    description: 'When a carrier is assigned to transport your order',
  },
  ORDER_READY_FOR_PICKUP: {
    title: 'Ready for Pickup',
    description: 'When your order is ready at the warehouse',
  },
  ORDER_REJECTED: {
    title: 'Order Rejected',
    description: 'When your order cannot be approved',
  },
  ORDER_ENROUTE: {
    title: 'Order Enroute',
    description: 'When your order is in transit',
  },
  ORDER_DELIVERED: {
    title: 'Order Delivered',
    description: 'When your order has been delivered',
  },
  ORDER_INVOICED: {
    title: 'Invoice Available',
    description: 'When your invoice is ready',
  },
  ORDER_PAID: {
    title: 'Payment Received',
    description: 'When your payment has been processed',
  },
  ORDER_CANCELLED: {
    title: 'Order Cancelled',
    description: 'When your order is cancelled',
  },
  ORDER_PREPAY_REQUIRED: {
    title: 'Prepayment Required',
    description: 'When prepayment is needed before approval',
  },
  ORDER_NOTE_FROM_ORG: {
    title: 'Notes from Supplier',
    description: 'When your supplier leaves a note on your order',
  },
  ORDER_NOTE_FROM_CUSTOMER: {
    title: 'Notes from Customer',
    description: 'When a customer leaves a note on an order',
  },
};

export default function NotificationPreferencesTab() {
  const { data: preferences = [], isLoading, isFetching } = useNotificationPreferences();
  const updatePreference = useUpdateNotificationPreference();

  const handleToggle = async (event: NotificationEvent, currentEnabled: boolean) => {
    try {
      await updatePreference.mutateAsync({
        event,
        channel: 'EMAIL',
        enabled: !currentEnabled,
      });
      toast.success(!currentEnabled ? 'Notification enabled' : 'Notification disabled');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to update preference');
    }
  };

  // Create a map of event -> enabled status
  const preferenceMap = useMemo(() => {
    const map = new Map<NotificationEvent, boolean>();
    preferences.forEach((pref) => {
      if (pref.channel === 'EMAIL') {
        map.set(pref.event, pref.enabled);
      }
    });
    return map;
  }, [preferences]);

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 12 }}>
        <div>
          <h2 className="card__title">Email Notifications</h2>
          <p className="card__subtitle">
            Choose which email notifications you'd like to receive about your orders
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="skeleton" style={{ height: 400 }} />
      ) : (
        <div className="notification-preferences card card--bleed-top">
          {/* Only show customer-visible events */}
          {CUSTOMER_VISIBLE_EVENTS.map((event) => {
            const { title, description } = EVENT_LABELS[event];
            const isEnabled = preferenceMap.get(event) ?? true; // Default to enabled

            return (
              <div key={event} className="notification-preference-item">
                <div className="notification-preference-item__content">
                  <div className="notification-preference-item__title">{title}</div>
                  <div className="notification-preference-item__description">{description}</div>
                </div>

                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleToggle(event, isEnabled)}
                    disabled={updatePreference.isPending}
                  />
                  <span className="toggle__slider" />
                </label>
              </div>
            );
          })}
        </div>
      )}

      {isFetching && (
        <div className="muted" style={{ marginTop: 8 }}>
          Refreshing…
        </div>
      )}

      {updatePreference.isPending && (
        <div className="muted" style={{ marginTop: 16, textAlign: 'center' }}>
          Updating preferences...
        </div>
      )}
    </div>
  );
}
